/* eslint-disable max-len */
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutlet } from 'react-router';
import type { SwiperClass } from 'swiper/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Typography } from '../Typography';
import styles from './Tab.module.scss';

interface BaseTab {
	name: string;
	tab: React.ReactNode;
}

interface TabWithRoute extends Omit<BaseTab, 'tab'> {
	route: string;
}

type TabModel = BaseTab[] | TabWithRoute[];

interface TabProps<T extends TabModel> {
	tabs: T;
	mode?: T extends BaseTab[] ? 'simple' : 'withRouting';
	onTabChange?: (index: number) => void;
	staticContent?: React.ReactNode;
	style?: React.CSSProperties;
	swiper?: T extends TabWithRoute ? never : boolean;
	tabIndex?: T extends TabWithRoute ? never : number;
}

interface ITab {
	<T extends TabModel>(props: TabProps<T>): React.ReactNode;
}

const Tab: ITab = <T extends TabModel>({
	tabs,
	mode = 'simple' as T extends BaseTab[] ? 'simple' : 'withRouting',
	style,
	staticContent,
	swiper,
	tabIndex,
	onTabChange,
}: TabProps<T>) => {
	const [currentIndex, setCurrentIndex] = useState<number>(tabIndex || 0);

	const navigate = useNavigate();
	const outlet = useOutlet();

	const swiperReference = useRef<SwiperClass | null>(null);

	const onSwiperInit = (swiper: SwiperClass) => {
		swiperReference.current = swiper;
		console.log(swiper);
	};

	const renderTabButton = useCallback(
		(item: T[0], index: number) => (
			<button
				key={index}
				className={`${styles.tabButton} ${index === currentIndex ? styles.active : ''}`}
				onClick={() => {
					onTabChange?.(index);

					if (mode === 'withRouting') {
						navigate((item as T extends TabWithRoute[] ? TabWithRoute : never).route);
						console.log((item as T extends TabWithRoute[] ? TabWithRoute : never).route);
					}

					if (tabIndex === undefined) {
						setCurrentIndex(index);
						swiperReference.current?.slideTo(index);
					}
				}}
			>
				<Typography variant='body2' variantS='body3' textTransform='uppercase' className={styles.tabButton__text}>
					{item.name}
				</Typography>
				<hr className={styles.hr} />
			</button>
		),
		[currentIndex],
	);

	useEffect(() => {
		if (tabIndex !== undefined) {
			setCurrentIndex(tabIndex);
			swiperReference.current?.slideTo(tabIndex);
		}
	}, [tabIndex]);

	return (
		<div className={styles.tab} style={{ ...style }}>
			<div className={styles.tabHeaders}>
				{swiper ? (
					<Swiper
						freeMode
						className={styles.swiper}
						wrapperClass={styles.swiperWrapper}
						slidesPerView='auto'
						initialSlide={0}
						breakpoints={{
							0: { threshold: 0 },
							1024: { threshold: 20 },
						}}
						onBeforeInit={onSwiperInit}
					>
						{tabs.map((item, index) => (
							<SwiperSlide key={`${item.name}`} className={styles.tabItems}>
								{renderTabButton(item, index)}
							</SwiperSlide>
						))}
					</Swiper>
				) : (
					tabs.map((t, index) => renderTabButton(t, index))
				)}
			</div>

			<motion.div layout className={styles.tabContentWrapper}>
				{staticContent}
				<AnimatePresence mode='wait'>
					<motion.div
						key={currentIndex}
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.3, ease: 'easeInOut' }}
					>
						{mode === 'withRouting' ? outlet : (tabs[currentIndex] as T extends BaseTab[] ? BaseTab : never).tab}
					</motion.div>
				</AnimatePresence>
			</motion.div>
		</div>
	);
};

export default Tab;
